import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useConfirmation } from '@/contexts/confirmation'
import { useModal } from '@/contexts/modal'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import {
    disableEmployee,
    getEmployeesList,
    resendConfirmLink,
    updateEmployeeProbationDate,
} from '../services'
import { getEmployeeListColumns } from '../utils/employee-list-columns'
import {
    getActions,
    getFilterItems,
    getPositionFilterItem,
} from '../utils/employee-list-config'

interface EmployeeFilters {
    employee_contract_type: string
    employee_seniority_level: string
    position_is_executive: string
    employee_gender: string
    employee_status: number[]
    birthday_range: string[]
    birthday_range_to: string[]
    start_date_range: string[]
    start_date_range_to: string[]
    end_date_range: string[]
    end_date_range_to: string[]
    department_id: number
    position_id: number
}

interface UseEmployeeListOptions {
    /** Force employee_status filter (e.g. [1]=working, [0]=resigned). Overrides URL filter */
    forcedStatus?: number[]
    /** URL prefix to isolate params/filter state per tab */
    urlPrefix?: string
    /** Default sort column (used when URL has no sort_by) */
    defaultSortBy?: string
    /** Default sort order: 'asc' | 'desc' */
    defaultSortOrder?: 'asc' | 'desc'
}

export const useEmployeeList = ({
    forcedStatus,
    urlPrefix,
    defaultSortBy = 'employee_status',
    defaultSortOrder,
}: UseEmployeeListOptions = {}) => {
    const navigate = useNavigate()
    const { setModal } = useModal()
    const { setConfirmation } = useConfirmation()
    const { t } = useTranslation()

    // URL search params
    const {
        params,
        filters,
        queryParams,
        setParam,
        setParams,
        setFilter,
        setFilters,
        searchInputProps,
    } = useUrlSearchParams<EmployeeFilters>({
        prefix: urlPrefix,
        defaults: {
            sort_by: defaultSortBy,
            ...(defaultSortOrder ? { sort_order: defaultSortOrder } : {}),
        },
        filterDefaults: {
            employee_contract_type: '',
            employee_seniority_level: '',
            position_is_executive: '',
            employee_gender: '',
            employee_status: forcedStatus ?? [],
            birthday_range: [],
            birthday_range_to: [],
            start_date_range: [],
            start_date_range_to: [],
            end_date_range: [],
            end_date_range_to: [],
            department_id: 0,
            position_id: 0,
        },
    })

    // If forcedStatus is set, override the filter value sent to API regardless of URL state
    const effectiveQueryParams = useMemo(() => {
        if (!forcedStatus) return queryParams
        try {
            const raw = queryParams.filters
                ? JSON.parse(atob(queryParams.filters))
                : {}
            const patched = { ...raw, employee_status: forcedStatus }
            return { ...queryParams, filters: btoa(JSON.stringify(patched)) }
        } catch {
            return queryParams
        }
    }, [queryParams, forcedStatus])

    // Form for filter selects
    const { control, setValue } = useForm<EmployeeFilters>({
        defaultValues: filters,
    })

    // Fetch data
    const { data, refetch, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.EMPLOYEES, effectiveQueryParams],
        queryFn: () => getEmployeesList(effectiveQueryParams),
        placeholderData: true,
    })

    const employees = data?.results || []
    const pagination = data?.pagination

    // Mutations
    const { mutateAsync } = useMutationWithHandler(
        { mutationFn: disableEmployee },
        {
            reactQueryKeys: QUERY_KEYS.EMPLOYEES,
            successMessage: 'employee_disabled_success',
            errorMessage: 'error-occurred',
            onSuccess: () => refetch(),
        }
    )

    const { mutateAsync: resend } = useMutationWithHandler(
        { mutationFn: resendConfirmLink },
        {
            reactQueryKeys: QUERY_KEYS.EMPLOYEES,
            successMessage: 'resend-success',
            errorMessage: 'error-occurred',
        }
    )

    const { mutateAsync: updateProbationDate } = useMutationWithHandler(
        { mutationFn: updateEmployeeProbationDate },
        {
            reactQueryKeys: QUERY_KEYS.EMPLOYEES,
            successMessage: 'probation_date_update_success',
            errorMessage: 'error-occurred',
            onSuccess: () => refetch(),
        }
    )

    // Memoized configs
    const columns = useMemo(() => getEmployeeListColumns(t), [t])

    const actions = useMemo(
        () =>
            getActions({
                t,
                navigate,
                setConfirmation,
                setModal,
                resend,
                updateProbationDate,
                mutateAsync,
            }),
        [
            t,
            navigate,
            setConfirmation,
            setModal,
            resend,
            updateProbationDate,
            mutateAsync,
        ]
    )

    const filterItems = useMemo(() => {
        const showEndDateRange = forcedStatus?.[0] === 0 // only on resigned tab
        const base = getFilterItems({
            t,
            setValue,
            setFilters,
            showEndDateRange,
        })
        return [...base, getPositionFilterItem(filters.department_id)]
    }, [t, filters.department_id, setValue, setFilters, forcedStatus])

    // Handlers
    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') => {
            setParams({ sort_by: sortBy, sort_order: sortOrder })
        },
        [setParams]
    )

    const handlePageChange = useCallback(
        (page: number) => {
            setParam('page', page, false)
        },
        [setParam]
    )

    const handlePageSizeChange = useCallback(
        (pageSize: number) => {
            setParams({ page_size: pageSize, page: 1 })
        },
        [setParams]
    )

    const stateComponent = renderState()

    return {
        // Data
        employees,
        pagination,
        stateComponent,
        // Form
        control,
        setFilter,
        setFilters,
        filters,
        // Configs
        columns,
        actions,
        filterItems,
        // Params
        params,
        queryParams: effectiveQueryParams,
        searchInputProps,
        // Handlers
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
        // Utils
        t,
    }
}
